import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./styling/WaterBalanceDemo.css";

/**
 * WaterBalanceDemo — homepage marketing preview of the Water Balance & Climate
 * capability. A miniature, self-contained version of the app's node-based
 * routing graph: illustrative facilities the visitor can drag, add to, wire
 * together and unwire.
 *
 * Deliberately NOT the real WaterBalanceGraph — no canvas, no scenario, no API,
 * no flow-rate editing, no node properties, no zoom/pan. But everything the
 * visitor sees or does here is the real thing: the node silhouettes, fills,
 * rims, icons, on-node labels, orthogonal rounded edge routing, neutral edge
 * colour and pill percentage labels are all ported from WaterBalanceGraph's
 * canvas renderer, connections start from the green handle on the node's right
 * edge and finish on the target node, and edges are click-to-select then
 * delete. Matching that screen matters more than convenience here — this is a
 * preview of it, so the two must not drift apart.
 *
 * Node positions are stored normalised (0..1) and painted in device pixels
 * against the measured stage, rather than in a fixed viewBox. A viewBox would
 * letterbox inside the wide showcase panel — leaving unused margins and, worse,
 * a draggable region smaller than the frame the visitor can see.
 */

/* ── Node styling ──────────────────────────────────────────────────────────
   Shapes, fills, rims and captions are WaterBalanceGraph's, verbatim: heaps are
   trapezoids (a pile), ponds are rounded basins sharing one basin blue with the
   rim carrying the sub-type, plants are rectangles with a cut corner. */
const TYPES = {
  heap: {
    shape: "heap",
    short: "Pad",
    fill: "#7A5C46",
    rim: "#A8825F",
    caption: null,
  },
  pls: {
    shape: "pond",
    short: "PLS",
    fill: "#35597A",
    rim: "#C4954A",
    caption: "Pregnant solution",
  },
  barren: {
    shape: "pond",
    short: "Barren",
    fill: "#35597A",
    rim: "#6FA3C9",
    caption: "Barren solution",
  },
  plant: {
    shape: "plant",
    short: "Plant",
    fill: "#5E4470",
    rim: "#9167A3",
    caption: null,
  },
};

const TYPE_ORDER = ["heap", "pls", "barren", "plant"];

/* Stroke-drawn glyphs on a 24-unit grid — the same path data the real graph
   paints onto its nodes and shows in its legend and add-node menu. */
const NODE_ICON_PATHS = {
  heap: ["M -11 6 L -4 -5 L 0 0 L 5 -8 L 11 6 Z"],
  pond: ["M -10 -3 q 5 -4 10 0 t 10 0", "M -10 4 q 5 -4 10 0 t 10 0"],
  plant: ["M -10 7 L -10 -1 L -5 2 L -5 -1 L 0 2 L 0 -6 L 7 -6 L 7 7 Z"],
};

const NodeGlyph = ({ shape, size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="-12 -12 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {(NODE_ICON_PATHS[shape] || NODE_ICON_PATHS.pond).map((d, i) => (
      <path key={i} d={d} />
    ))}
  </svg>
);

/* ── Geometry ──────────────────────────────────────────────────────────────
   Node boxes keep WaterBalanceGraph's aspect ratios, but `size` is derived from
   the measured stage instead of being fixed: unlike the circles this replaced,
   a node is now four `size` units wide, and a box scaled for the desktop
   showcase panel covers a third of a phone-width stage. The real graph solves
   the same problem with zoom-to-fit, which this preview deliberately has no
   room for. */
const NODE_ASPECT = 2; // half-width  = size * NODE_ASPECT
const NODE_HEIGHT = 0.86; // half-height = size * NODE_HEIGHT
const HEAP_TALLER = 1.2; // heaps get a little more height for the trapezoid
const NODE_SIZE_MIN = 21;
const NODE_SIZE_MAX = 30;
const STAGE_PER_SIZE = 15; // stage width that buys one unit of node size

/* Handle geometry mirrors WaterBalanceGraph: centre sits 5px off the node's
   right edge, with a hit radius far larger than the visible dot. */
const HANDLE_R = 8;
const HANDLE_HIT_R = 15;

/* Keep a dragged node fully inside the stage, with room for its handle — and,
   along the bottom, for the floating hint, which would otherwise sit on top of
   whatever facility is nearest the bottom edge. On a short stage that strip is
   a fifth of the usable height, so the hint is dropped instead — and the
   decision is made here, not in a CSS breakpoint, so the reserved space and the
   hint itself can never disagree. */
const HINT_CLEARANCE = 36;
const HINT_MIN_STAGE_H = 240;

const EDGE_CORNER_RADIUS = 14;

const makeGeometry = (stageW, stageH) => {
  const size = Math.max(
    NODE_SIZE_MIN,
    Math.min(NODE_SIZE_MAX, (stageW || 600) / STAGE_PER_SIZE)
  );
  const halfW = size * NODE_ASPECT;
  const maxHalfH = size * NODE_HEIGHT * HEAP_TALLER;
  const showHint = stageH >= HINT_MIN_STAGE_H;
  return {
    size,
    halfW,
    showHint,
    halfH: (type) =>
      size * NODE_HEIGHT * (TYPES[type].shape === "heap" ? HEAP_TALLER : 1),
    handleDx: halfW + 5,
    padL: halfW + 8,
    padR: halfW + HANDLE_HIT_R,
    padTop: maxHalfH + 8,
    padBottom: maxHalfH + 8 + (showHint ? HINT_CLEARANCE : 0),
    nameSize: Math.max(10.5, 12 * (size / NODE_SIZE_MAX)),
    captionSize: Math.max(8.5, 9.5 * (size / NODE_SIZE_MAX)),
    /* The pond sub-type caption is the first thing to go when the board gets
       small — the same call the real graph's legend makes. */
    showCaption: size >= 24,
  };
};

const INITIAL_NODES = [
  { id: "pad", label: "Pad A", type: "heap", nx: 0.5, ny: 0.14 },
  { id: "pls", label: "PLS Pond", type: "pls", nx: 0.83, ny: 0.5 },
  { id: "plant", label: "CIC Plant", type: "plant", nx: 0.5, ny: 0.86 },
  { id: "barren", label: "Barren Pond", type: "barren", nx: 0.17, ny: 0.5 },
];

const INITIAL_EDGES = [
  { from: "barren", to: "pad" },
  { from: "pad", to: "pls" },
  { from: "pls", to: "plant" },
  { from: "plant", to: "barren" },
];

const edgeKey = (e) => `${e.from}->${e.to}`;

/** Next display name for a type, given what's already on the board. */
const labelFor = (type, existing) => {
  const n = existing.filter((e) => e.type === type).length;
  if (type === "heap") return `Pad ${String.fromCharCode(65 + n)}`;
  if (type === "pls") return `PLS Pond ${n + 1}`;
  if (type === "barren") return `Barren Pond ${n + 1}`;
  return `Plant ${n + 1}`;
};

/* ── Shapes ────────────────────────────────────────────────────────────────
   Ported from WaterBalanceGraph's nodeShapePath. Drawn about the node's own
   origin, so each node group only needs a translate. */
const shapeD = (type, g) => {
  const halfH = g.halfH(type);
  const L = -g.halfW;
  const R = g.halfW;
  const T = -halfH;
  const B = halfH;

  if (TYPES[type].shape === "heap") {
    const inset = g.halfW * 0.42;
    return `M ${L} ${B} L ${R} ${B} L ${R - inset} ${T} L ${L + inset} ${T} Z`;
  }

  if (TYPES[type].shape === "plant") {
    const r = Math.min(6, halfH * 0.25);
    const cut = halfH * 0.55;
    return (
      `M ${L + r} ${T} L ${R - cut} ${T} L ${R} ${T + cut} L ${R} ${B - r} ` +
      `Q ${R} ${B} ${R - r} ${B} L ${L + r} ${B} Q ${L} ${B} ${L} ${B - r} ` +
      `L ${L} ${T + r} Q ${L} ${T} ${L + r} ${T} Z`
    );
  }

  const r = Math.min(halfH * 0.45, g.halfW);
  return (
    `M ${L + r} ${T} L ${R - r} ${T} A ${r} ${r} 0 0 1 ${R} ${T + r} ` +
    `L ${R} ${B - r} A ${r} ${r} 0 0 1 ${R - r} ${B} ` +
    `L ${L + r} ${B} A ${r} ${r} 0 0 1 ${L} ${B - r} ` +
    `L ${L} ${T + r} A ${r} ${r} 0 0 1 ${L + r} ${T} Z`
  );
};

/* ── Edge routing ──────────────────────────────────────────────────────────
   Orthogonal with rounded corners — the convention these users already read
   flowsheets in, and the shape the real graph draws. Ported from
   WaterBalanceGraph's computeEdgePoints, minus the parallel-sibling offset:
   this board never holds two edges between the same pair. */
const computeEdgePoints = (source, target, g) => {
  const dx = target.x - source.x;
  const dy = target.y - source.y;

  if (Math.abs(dx) >= Math.abs(dy)) {
    const sign = dx >= 0 ? 1 : -1;
    const sx = source.x + sign * g.halfW;
    const tx = target.x - sign * g.halfW;
    // Already level with each other — a single straight run reads better than
    // a dog-leg with two zero-length corners.
    if (Math.abs(source.y - target.y) < 1) {
      return [
        { x: sx, y: source.y },
        { x: tx, y: target.y },
      ];
    }
    const midX = (sx + tx) / 2;
    return [
      { x: sx, y: source.y },
      { x: midX, y: source.y },
      { x: midX, y: target.y },
      { x: tx, y: target.y },
    ];
  }

  const sign = dy >= 0 ? 1 : -1;
  const sy = source.y + sign * g.halfH(source.type);
  const ty = target.y - sign * g.halfH(target.type);
  if (Math.abs(source.x - target.x) < 1) {
    return [
      { x: source.x, y: sy },
      { x: target.x, y: ty },
    ];
  }
  const midY = (sy + ty) / 2;
  return [
    { x: source.x, y: sy },
    { x: source.x, y: midY },
    { x: target.x, y: midY },
    { x: target.x, y: ty },
  ];
};

/** Polyline -> path data with rounded corners, each radius clamped so it can
    never exceed half of either adjoining segment. */
const roundedPathD = (pts, radius) => {
  if (!pts || pts.length < 2) return "";
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length - 1; i += 1) {
    const prev = pts[i - 1];
    const cur = pts[i];
    const next = pts[i + 1];
    const inLen = Math.hypot(cur.x - prev.x, cur.y - prev.y) || 1;
    const outLen = Math.hypot(next.x - cur.x, next.y - cur.y) || 1;
    const r = Math.max(0, Math.min(radius, inLen / 2, outLen / 2));
    const ax = cur.x + ((prev.x - cur.x) / inLen) * r;
    const ay = cur.y + ((prev.y - cur.y) / inLen) * r;
    const bx = cur.x + ((next.x - cur.x) / outLen) * r;
    const by = cur.y + ((next.y - cur.y) / outLen) * r;
    d += ` L ${ax} ${ay} Q ${cur.x} ${cur.y} ${bx} ${by}`;
  }
  const end = pts[pts.length - 1];
  return `${d} L ${end.x} ${end.y}`;
};

/** Arc-length midpoint of a polyline — where the percentage pill sits. */
const polylineMidpoint = (pts) => {
  let total = 0;
  for (let i = 1; i < pts.length; i += 1) {
    total += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
  }
  let walked = 0;
  const half = total / 2;
  for (let i = 1; i < pts.length; i += 1) {
    const segLen = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    if (walked + segLen >= half && segLen > 0) {
      const t = (half - walked) / segLen;
      return {
        x: pts[i - 1].x + (pts[i].x - pts[i - 1].x) * t,
        y: pts[i - 1].y + (pts[i].y - pts[i - 1].y) * t,
      };
    }
    walked += segLen;
  }
  return pts[pts.length - 1];
};

/** Pill width from the character count — the real graph's formula. */
const edgeLabelWidth = (text) => 18 + String(text).length * 6.9;

/* ── Text fitting ──────────────────────────────────────────────────────────
   SVG text does not clip itself, so a long facility name would run off its
   node. Measure with a throwaway 2D context — the same ellipsis behaviour as
   WaterBalanceGraph's fitText. The font stack is repeated from index.css: a
   measurement taken in a different family than the one that renders would trim
   to the wrong width. */
const FONT_STACK =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', " +
  "'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";
const nameFont = (px) => `600 ${px}px ${FONT_STACK}`;
const captionFont = (px) => `${px}px ${FONT_STACK}`;

let measureCtx;
const getMeasureCtx = () => {
  if (measureCtx !== undefined) return measureCtx;
  try {
    measureCtx = document.createElement("canvas").getContext("2d") || null;
  } catch {
    measureCtx = null;
  }
  return measureCtx;
};

const fitText = (text, maxWidth, font) => {
  const str = String(text ?? "");
  const ctx = getMeasureCtx();
  if (!ctx) return str;
  ctx.font = font;
  const full = ctx.measureText(str).width;
  /* jsdom measures everything as 0 — leave the label alone rather than
     ellipsing it down to a single character. */
  if (!full || full <= maxWidth) return str;
  let out = str;
  while (out.length > 1 && ctx.measureText(`${out}…`).width > maxWidth) {
    out = out.slice(0, -1);
  }
  return `${out}…`;
};

const WaterBalanceDemo = () => {
  const [nodes, setNodes] = useState(INITIAL_NODES);
  const [edges, setEdges] = useState(INITIAL_EDGES);
  const [addType, setAddType] = useState("heap");
  const [connectFrom, setConnectFrom] = useState(null);
  const [pointer, setPointer] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const stageRef = useRef(null);
  const nextId = useRef(0);
  const armPos = useRef(null);

  /* Measure the stage so node coordinates can be painted in real pixels. */
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize((prev) =>
        Math.abs(prev.w - width) < 0.5 && Math.abs(prev.h - height) < 0.5
          ? prev
          : { w: width, h: height }
      );
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { w, h } = size;
  const ready = w > 0 && h > 0;

  /* Node box, padding and label sizes all follow the measured stage. */
  const geom = useMemo(() => makeGeometry(w, h), [w, h]);

  /** Normalised position -> pixel centre, clamped inside the stage. */
  const toPx = useCallback(
    (n) => ({
      x: Math.min(w - geom.padR, Math.max(geom.padL, n.nx * w)),
      y: Math.min(h - geom.padBottom, Math.max(geom.padTop, n.ny * h)),
    }),
    [w, h, geom]
  );

  const placed = useMemo(
    () => (ready ? nodes.map((n) => ({ ...n, ...toPx(n) })) : []),
    [nodes, ready, toPx]
  );

  const byId = useMemo(
    () => Object.fromEntries(placed.map((n) => [n.id, n])),
    [placed]
  );

  /* A node's outgoing flow is split evenly across its edges, so wiring up a
     second destination re-labels the first — and deleting one hands its share
     back. Same behaviour the real routing graph shows when a facility fans
     out. */
  const shareFor = useCallback(
    (from) => 100 / edges.filter((e) => e.from === from).length,
    [edges]
  );

  const pointerToPx = useCallback((evt) => {
    const el = stageRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return { x: evt.clientX - rect.left, y: evt.clientY - rect.top };
  }, []);

  /* ── Moving ──────────────────────────────────────────────────── */

  const handleNodePointerDown = (id) => (evt) => {
    evt.stopPropagation();
    if (connectFrom) return; // a click finishes the connection instead
    evt.currentTarget.setPointerCapture(evt.pointerId);
    setDragId(id);
  };

  const handleStagePointerMove = (evt) => {
    const p = pointerToPx(evt);
    if (!p || !ready) return;
    if (connectFrom) setPointer(p); // rubber-band follows the pointer
    if (!dragId) return;
    setNodes((prev) =>
      prev.map((n) => (n.id === dragId ? { ...n, nx: p.x / w, ny: p.y / h } : n))
    );
  };

  /* The real graph is click-handle-then-click-target, but releasing straight
     onto a facility is the other thing people try, so accept both: a release
     that travelled more than a few pixels is read as a drag and completes on
     whatever it landed on; anything shorter is the arming click and leaves the
     connection armed for a second click. */
  const DRAG_CONNECT_SLOP = 8;

  const handleStagePointerUp = (evt) => {
    setDragId(null);
    if (!connectFrom) return;
    const p = pointerToPx(evt);
    const from = armPos.current;
    if (!p || !from) return;
    if (Math.hypot(p.x - from.x, p.y - from.y) <= DRAG_CONNECT_SLOP) return;
    const target = evt.target?.closest?.(".wb-demo__node");
    if (target?.dataset?.nodeId) completeConnection(target.dataset.nodeId);
    else cancelConnection();
  };

  /* Release a stuck drag if the pointer is lost outside the component. */
  useEffect(() => {
    if (!dragId) return undefined;
    const clear = () => setDragId(null);
    window.addEventListener("pointerup", clear);
    window.addEventListener("pointercancel", clear);
    return () => {
      window.removeEventListener("pointerup", clear);
      window.removeEventListener("pointercancel", clear);
    };
  }, [dragId]);

  /* ── Wiring ──────────────────────────────────────────────────── */

  const startConnection = (id) => (evt) => {
    evt.stopPropagation();
    setSelectedEdge(null);
    setConnectFrom(id);
    const p = pointerToPx(evt);
    if (p) {
      setPointer(p);
      armPos.current = p;
    }
  };

  /* stopPropagation on the pointerdown doesn't help here: the browser fires a
     separate click afterwards which bubbles to the node (self-connect) and the
     stage (cancel), disarming the connection the instant it was armed. */
  const swallowClick = (evt) => evt.stopPropagation();

  const cancelConnection = useCallback(() => {
    setConnectFrom(null);
    setPointer(null);
  }, []);

  const completeConnection = (targetId) => {
    if (!connectFrom || connectFrom === targetId) {
      cancelConnection();
      return;
    }
    setEdges((prev) =>
      prev.some((e) => e.from === connectFrom && e.to === targetId)
        ? prev
        : [...prev, { from: connectFrom, to: targetId }]
    );
    cancelConnection();
  };

  const handleNodeClick = (id) => (evt) => {
    if (!connectFrom) return;
    evt.stopPropagation();
    completeConnection(id);
  };

  /* ── Edges ───────────────────────────────────────────────────── */

  const selectEdge = (key) => (evt) => {
    evt.stopPropagation();
    if (connectFrom) return;
    setSelectedEdge(key);
  };

  const deleteEdge = useCallback((key) => {
    setEdges((prev) => prev.filter((e) => edgeKey(e) !== key));
    setSelectedEdge(null);
  }, []);

  /* Delete/Backspace removes the selected edge, matching the real graph's
     keyboard shortcut. Ignored while typing anywhere on the page. */
  useEffect(() => {
    if (!selectedEdge) return undefined;
    const onKey = (evt) => {
      if (evt.key !== "Delete" && evt.key !== "Backspace") return;
      const tag = evt.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || evt.target?.isContentEditable) return;
      evt.preventDefault();
      deleteEdge(selectedEdge);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedEdge, deleteEdge]);

  /* Escape backs out of whichever mode is active. */
  useEffect(() => {
    const onKey = (evt) => {
      if (evt.key !== "Escape") return;
      cancelConnection();
      setSelectedEdge(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cancelConnection]);

  /* ── Adding ──────────────────────────────────────────────────── */

  /* Drop a facility of the selected type, wired in from whichever facility is
     closest, so the circuit never gains an orphan. Extra links are the green
     handle's job. */
  const addNodeAt = useCallback((nx, ny, type) => {
    setNodes((prevNodes) => {
      nextId.current += 1;
      const id = `n-${nextId.current}`;
      const nearest = prevNodes.reduce((best, n) => {
        const d = (n.nx - nx) ** 2 + (n.ny - ny) ** 2;
        return !best || d < best.d ? { n, d } : best;
      }, null);
      if (nearest) {
        setEdges((prevEdges) => [...prevEdges, { from: nearest.n.id, to: id }]);
      }
      return [...prevNodes, { id, label: labelFor(type, prevNodes), type, nx, ny }];
    });
  }, []);

  /* The button drops the new facility in whichever corner is emptiest, so it
     never lands on top of an existing one. */
  const handleAddClick = () => {
    const corners = [
      { nx: 0.16, ny: 0.16 },
      { nx: 0.84, ny: 0.16 },
      { nx: 0.16, ny: 0.84 },
      { nx: 0.84, ny: 0.84 },
    ];
    const best = corners.reduce((acc, c) => {
      const nearest = Math.min(
        ...nodes.map((n) => (n.nx - c.nx) ** 2 + (n.ny - c.ny) ** 2)
      );
      return !acc || nearest > acc.nearest ? { c, nearest } : acc;
    }, null);
    addNodeAt(best.c.nx, best.c.ny, addType);
  };

  const handleStageDoubleClick = (evt) => {
    if (!ready || connectFrom) return;
    const p = pointerToPx(evt);
    if (p) addNodeAt(p.x / w, p.y / h, addType);
  };

  const handleStageClick = () => {
    cancelConnection();
    setSelectedEdge(null);
  };

  const handleReset = () => {
    setNodes(INITIAL_NODES);
    setEdges(INITIAL_EDGES);
    cancelConnection();
    setSelectedEdge(null);
    nextId.current = 0;
  };

  const pristine =
    nodes.length === INITIAL_NODES.length && edges.length === INITIAL_EDGES.length;

  const source = connectFrom ? byId[connectFrom] : null;

  let hint = "Drag a facility · double-click to add";
  if (connectFrom) hint = "Click the destination facility";
  else if (selectedEdge) hint = "Click ✕ or press Delete to remove";

  return (
    <div className="wb-demo">
      {/* Control bar borrows the real graph's floating-toolbar chrome, and each
          type carries the same glyph its nodes are drawn with — so the picker
          doubles as the legend the canvas shows bottom-left. */}
      <div className="wb-demo__toolbar" role="group" aria-label="Water balance demo controls">
        {TYPE_ORDER.map((t) => (
          <button
            key={t}
            type="button"
            className={`wb-demo__btn wb-demo__btn--type${addType === t ? " is-active" : ""}`}
            onClick={() => setAddType(t)}
            aria-pressed={addType === t}
          >
            <span className="wb-demo__btn-icon" style={{ color: TYPES[t].rim }}>
              <NodeGlyph shape={TYPES[t].shape} size={15} />
            </span>
            {TYPES[t].short}
          </button>
        ))}

        <span className="wb-demo__divider" />

        <button
          type="button"
          className="wb-demo__btn wb-demo__btn--primary"
          onClick={handleAddClick}
        >
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add node
        </button>
        <button
          type="button"
          className="wb-demo__btn"
          onClick={handleReset}
          disabled={pristine}
        >
          Reset
        </button>
      </div>

      <div
        className={`wb-demo__stage${connectFrom ? " is-connecting" : ""}`}
        ref={stageRef}
        onClick={handleStageClick}
        onDoubleClick={handleStageDoubleClick}
        onPointerMove={handleStagePointerMove}
        onPointerUp={handleStagePointerUp}
        onPointerCancel={() => setDragId(null)}
      >
        <svg
          className="wb-demo__svg"
          role="img"
          aria-label="Interactive sample water balance circuit. Drag a facility to move it, add pads, ponds and plants, drag from a facility's green handle to connect it to another, and click an edge to delete it."
        >
          <defs>
            {[
              ["wb-demo-arrow", "wb-demo__arrowhead"],
              ["wb-demo-arrow-selected", "wb-demo__arrowhead is-selected"],
            ].map(([id, cls]) => (
              <marker
                key={id}
                id={id}
                viewBox="0 0 10 10"
                refX="9.5"
                refY="5"
                /* userSpaceOnUse so the head keeps one size whether or not the
                   edge is emphasised — markerUnits defaults to strokeWidth. */
                markerUnits="userSpaceOnUse"
                markerWidth="12"
                markerHeight="12"
                orient="auto"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" className={cls} />
              </marker>
            ))}
            {/* The same soft drop shadow the canvas paints under each node. */}
            <filter id="wb-demo-node-shadow" x="-30%" y="-40%" width="160%" height="190%">
              <feDropShadow dx="0" dy="3" stdDeviation="5" floodColor="#000" floodOpacity="0.55" />
            </filter>
          </defs>

          {ready &&
            edges.map((e) => {
              const a = byId[e.from];
              const b = byId[e.to];
              if (!a || !b) return null;
              const key = edgeKey(e);
              const isSelected = selectedEdge === key;
              const points = computeEdgePoints(a, b, geom);
              const d = roundedPathD(points, EDGE_CORNER_RADIUS);
              const mid = polylineMidpoint(points);
              const text = `${shareFor(e.from).toFixed(1)}%`;
              const pillW = edgeLabelWidth(text);
              return (
                <g
                  key={key}
                  className={`wb-demo__edge${isSelected ? " is-selected" : ""}`}
                  onClick={selectEdge(key)}
                >
                  <path
                    className="wb-demo__edge-line"
                    d={d}
                    markerEnd={`url(#wb-demo-arrow${isSelected ? "-selected" : ""})`}
                  />
                  {/* Invisible fat line so the edge is realistically clickable. */}
                  <path className="wb-demo__edge-hit" d={d} />
                  {isSelected ? (
                    <g
                      className="wb-demo__edge-delete"
                      transform={`translate(${mid.x} ${mid.y})`}
                      onClick={(evt) => {
                        evt.stopPropagation();
                        deleteEdge(key);
                      }}
                      role="button"
                      aria-label="Delete this flow"
                    >
                      <circle r="11" />
                      <path d="M -4 -4 L 4 4 M 4 -4 L -4 4" />
                    </g>
                  ) : (
                    /* Percentage label — a rounded pill sitting on the line,
                       matching the real graph's. */
                    <g className="wb-demo__edge-pill" transform={`translate(${mid.x} ${mid.y})`}>
                      <rect x={-pillW / 2} y="-11" width={pillW} height="22" rx="11" />
                      <text y="4">{text}</text>
                    </g>
                  )}
                </g>
              );
            })}

          {/* Rubber band while a connection is being drawn. */}
          {source && pointer && (
            <line
              className="wb-demo__rubber"
              x1={source.x + geom.handleDx}
              y1={source.y}
              x2={pointer.x}
              y2={pointer.y}
            />
          )}

          {placed.map((n) => {
            const cfg = TYPES[n.type];
            const halfH = geom.halfH(n.type);
            /* A heap is a trapezoid, so its usable width at the vertical centre
               is narrower than the box — inset the content or the name runs out
               over the sloped sides. */
            const inner = cfg.shape === "heap" ? geom.halfW * 0.79 : geom.halfW;
            const iconX = -inner * 0.72;
            const textLeft = -inner * 0.42;
            const textWidth = inner * 1.42 - 8;
            const iconScale = Math.min(1.15, halfH / 28);
            const caption = geom.showCaption ? cfg.caption : null;
            return (
              <g
                key={n.id}
                data-node-id={n.id}
                className={
                  "wb-demo__node" +
                  (dragId === n.id ? " is-dragging" : "") +
                  (connectFrom === n.id ? " is-source" : "")
                }
                transform={`translate(${n.x} ${n.y})`}
                onPointerDown={handleNodePointerDown(n.id)}
                onClick={handleNodeClick(n.id)}
              >
                <path
                  className="wb-demo__node-body"
                  d={shapeD(n.type, geom)}
                  fill={cfg.fill}
                  filter="url(#wb-demo-node-shadow)"
                />
                {/* Rim — carries the pond sub-type, or the connection-source state. */}
                <path className="wb-demo__node-rim" d={shapeD(n.type, geom)} stroke={cfg.rim} />

                {/* Icon and name ride ON the node, as they do on the canvas. */}
                <g className="wb-demo__glyph" transform={`translate(${iconX} 0) scale(${iconScale})`}>
                  {(NODE_ICON_PATHS[cfg.shape] || NODE_ICON_PATHS.pond).map((d, i) => (
                    <path key={i} d={d} />
                  ))}
                </g>
                <text
                  className="wb-demo__name"
                  x={textLeft}
                  y={caption ? -3 : geom.nameSize * 0.34}
                  style={{ fontSize: `${geom.nameSize}px` }}
                >
                  {fitText(n.label, textWidth, nameFont(geom.nameSize))}
                </text>
                {caption && (
                  <text
                    className="wb-demo__caption"
                    x={textLeft}
                    y="12"
                    style={{ fontSize: `${geom.captionSize}px` }}
                  >
                    {fitText(caption, textWidth, captionFont(geom.captionSize))}
                  </text>
                )}

                {/* Connection handle — same place as WaterBalanceGraph's. */}
                <g
                  className="wb-demo__handle"
                  onPointerDown={startConnection(n.id)}
                  onClick={swallowClick}
                  role="button"
                  aria-label={`Draw a flow from ${n.label}`}
                >
                  <circle
                    className="wb-demo__handle-hit"
                    cx={geom.handleDx}
                    cy="0"
                    r={HANDLE_HIT_R}
                  />
                  <circle
                    className="wb-demo__handle-dot"
                    cx={geom.handleDx}
                    cy="0"
                    r={HANDLE_R}
                  />
                </g>
              </g>
            );
          })}
        </svg>

        {geom.showHint && <span className="wb-demo__hint">{hint}</span>}
      </div>
    </div>
  );
};

export default WaterBalanceDemo;
